import * as React from "react";
import { connect } from "react-redux";
import { Button } from "semantic-ui-react";

import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { HealthSurveillanceSectionProps } from "@teamprevent/common/healthSurveillance/models";
import { calculateBmi } from "@teamprevent/common/bmi/calculateBmi";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { checkExistenceOfQuestionWithId } from "@teamprevent/common/questionnaires/checkExistenceOfQuestionWithId";
import { MultiCheckboxQuestion, MultiCheckbox } from "@teamprevent/common/questionnaires/components/questions/MultiCheckboxQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { FilesQuestion } from "@teamprevent/common/questionnaires/components/questions/FilesQuestion";
import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { HealthSurveillanceQuestionConfig } from "@teamprevent/common/healthSurveillance";

import * as questionIds from "../../healthSurveillance/consultationQuestions/questionIds";
import {
    bmiQuestion, diastolicFiveQuestion, diastolicFourQuestion, diastolicThreeQuestion, diastolicTwoQuestion, systolicFiveQuestion,
    systolicFourQuestion, systolicThreeQuestion, systolicTwoQuestion
} from "../../healthSurveillance/consultationQuestions/introQuestions";

import "../../healthSurveillance/styles/health-surveillance-consultation-health-questions-section.scss";
import {
    FailOption,
    PassOption,
    PulseRhythmIrregular,
    PulseRhythmRegular,
    distanceTestResultOptions,
    eyeVisionPeripheralFieldOptions,
    intermediateNearTestResultOptions,
    visionHorizontalFieldOptions
} from "../../healthSurveillance/models";

const otherTestTypeAnswer = "Other";
const otherRoleTypeAnswer = "Other";
const noVisualAidAnswer = "None";
const otherVisualAidAnswer = "Other visual aid";

const sectionId = "TechnicianNotes";

interface AdditionalProps {
    fileUploadApi: string;
}

type AllProps = HealthSurveillanceSectionProps & AdditionalProps;

// eslint-disable-next-line max-len
const TechnicianNotesSectionUnconnected: React.FunctionComponent<AllProps> = (props) => {
    const [additionalBloodPressureReadings, setAdditionalBloodPressureReadings] = React.useState(0);

    const updateBmiQuestion = (heightInCms: number, weightInKgs: number) => {
        props.updateAnswer({
            sectionId,
            id: bmiQuestion.id,
            questionnaireKey: props.questionnaireKey,
            question: {
                question: bmiQuestion.questionText,
                isValid: true,
                answer: weightInKgs && heightInCms && calculateBmi({ weightInKgs, heightInCms })
            }
        });
    };

    const onHeightChanged = (heightInCms: number) => {
        const weightInKgs = props.questions[questionIds.Weight].answer as number;
        updateBmiQuestion(heightInCms, weightInKgs);
    };
    const onWeightChanged = (weightInKgs: number) => {
        const heightInCms = props.questions[questionIds.Height].answer as number;
        updateBmiQuestion(heightInCms, weightInKgs);
    };

    const addBloodPressureReading = () => {
        if (additionalBloodPressureReadings < 4) {
            setAdditionalBloodPressureReadings(additionalBloodPressureReadings + 1);
        }
    };
    const removeBloodPressureReading = () => {
        if (additionalBloodPressureReadings > 0) {
            let bloodPressureQuestions: HealthSurveillanceQuestionConfig[] = [];
            switch (additionalBloodPressureReadings) {
                case 1:
                    bloodPressureQuestions = [systolicTwoQuestion, diastolicTwoQuestion];
                    break;
                case 2:
                    bloodPressureQuestions = [systolicThreeQuestion, diastolicThreeQuestion];
                    break;
                case 3:
                    bloodPressureQuestions = [systolicFourQuestion, diastolicFourQuestion];
                    break;
                case 4:
                    bloodPressureQuestions = [systolicFiveQuestion, diastolicFiveQuestion];
                    break;
                default:
                    break;
            }

            bloodPressureQuestions.forEach(bloodPressureQuestion => props.updateAnswer({
                sectionId,
                id: bloodPressureQuestion.id,
                questionnaireKey: props.questionnaireKey,
                question: {
                    question: bloodPressureQuestion.questionText,
                    isValid: true,
                    answer: undefined
                }
            }));

            setAdditionalBloodPressureReadings(additionalBloodPressureReadings - 1);
        }
    };

    React.useEffect(() => {
        if (additionalBloodPressureReadings === 0 && (
            props.questions[questionIds.SystolicBloodPressureTwo]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureTwo]?.answer ||
            props.questions[questionIds.SystolicBloodPressureThree]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureThree]?.answer ||
            props.questions[questionIds.SystolicBloodPressureFour]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureFour]?.answer ||
            props.questions[questionIds.SystolicBloodPressureFive]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureFive]?.answer)) {
            if (props.questions[questionIds.SystolicBloodPressureTwo]?.answer || props.questions[questionIds.DiastolicBloodPressureTwo]?.answer) {
                setAdditionalBloodPressureReadings(1);
            }
            if (props.questions[questionIds.SystolicBloodPressureThree]?.answer || props.questions[questionIds.DiastolicBloodPressureThree]?.answer) {
                setAdditionalBloodPressureReadings(2);
            }
            if (props.questions[questionIds.SystolicBloodPressureFour]?.answer || props.questions[questionIds.DiastolicBloodPressureFour]?.answer) {
                setAdditionalBloodPressureReadings(3);
            }
            if (props.questions[questionIds.SystolicBloodPressureFive]?.answer || props.questions[questionIds.DiastolicBloodPressureFive]?.answer) {
                setAdditionalBloodPressureReadings(4);
            }
        }
    }, [props.questions]);

    const questions = Object.values(props.questions || {});
    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            id={sectionId}
            title="Technician Notes"
        >
            {YesNoWithFollowUpQuestion({
                readonly: props.readonly,
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HealthUnableToCarryOut),
                    getQuestionConfig(props.questionConfigs, questionIds.HealthUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: true,
                questions: props.questions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.HealthUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OnReviewOfQuestionnaireByEmployeeHaveTheyTickedYesToAny)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                {/* eslint-disable-next-line max-len */}
                {getAnswerToQuestionWithId(questions, questionIds.OnReviewOfQuestionnaireByEmployeeHaveTheyTickedYesToAny) === MultiButtonQuestion.YesOption && <>
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PleaseProvideFurtherInformationOnTheConditionSymptomsHighlighted)}
                        mandatory
                        multiline
                        outdated={{
                            isOutdated: false,
                            questionnaireKey: props.questionnaireKey,
                            sectionId
                        }}
                    />
                </>}
                <NumberQuestion
                    min={130}
                    max={220}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Height)}
                    mandatory
                    onAnswerChange={onHeightChanged}
                />
                <NumberQuestion
                    min={25}
                    max={635}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Weight)}
                    mandatory
                    onAnswerChange={onWeightChanged}
                />
                <NumberQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Bmi)}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressure)}
                    mandatory
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressure)}
                    mandatory
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureTwo)}
                    mandatory={additionalBloodPressureReadings >= 1}
                    hidden={additionalBloodPressureReadings < 1}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureTwo)}
                    mandatory={additionalBloodPressureReadings >= 1}
                    hidden={additionalBloodPressureReadings < 1}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureThree)}
                    mandatory={additionalBloodPressureReadings >= 2}
                    hidden={additionalBloodPressureReadings < 2}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureThree)}
                    mandatory={additionalBloodPressureReadings >= 2}
                    hidden={additionalBloodPressureReadings < 2}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureFour)}
                    mandatory={additionalBloodPressureReadings >= 3}
                    hidden={additionalBloodPressureReadings < 3}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureFour)}
                    mandatory={additionalBloodPressureReadings >= 3}
                    hidden={additionalBloodPressureReadings < 3}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureFive)}
                    mandatory={additionalBloodPressureReadings >= 4}
                    hidden={additionalBloodPressureReadings < 4}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureFive)}
                    mandatory={additionalBloodPressureReadings >= 4}
                    hidden={additionalBloodPressureReadings < 4}
                />
                {!props.readonly && additionalBloodPressureReadings < 4 ? (
                    <Button onClick={addBloodPressureReading} content="Add Another Blood Pressure Reading" secondary />
                ) : ("")}
                {!props.readonly && additionalBloodPressureReadings > 0 ? (
                    <Button onClick={removeBloodPressureReading} content="Remove Last Blood Pressure Reading" secondary />
                ) : ("")}
                {!props.readonly ? (<div className={"fields-divider"} />) : ("")}
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseRate)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseRhythm)}
                    values={[PulseRhythmRegular, PulseRhythmIrregular]}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                <NumberQuestion
                    min={0.5}
                    max={6}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Cholesterol)}
                />
                {(!props.readonly || checkExistenceOfQuestionWithId(questions, questionIds.BloodSugar)) &&
                    <NumberQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BloodSugar)}
                    />
                }
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Psa)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.GPSelfReferralLetterIssuedRegardingBloodPressureOrPulse)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                {(!props.readonly || checkExistenceOfQuestionWithId(questions, questionIds.Whisper)) && <>
                    <MultiButtonQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Whisper)}
                        mandatory
                    />
                    {getAnswerToQuestionWithId(questions, questionIds.Whisper) === MultiButtonQuestion.YesOption && <>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhisperLeftEarPassed)}
                            values={[PassOption, FailOption]}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhisperRightEarPassed)}
                            values={[PassOption, FailOption]}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BloodLeftEar)}
                            values={[PassOption, FailOption]}
                            mandatory
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: props.questionnaireKey,
                                sectionId
                            }}
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BloodRightEar)}
                            values={[PassOption, FailOption]}
                            mandatory
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: props.questionnaireKey,
                                sectionId
                            }}
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.GPSelefReferralLetterIssuedRegardingWhisperTestResult)}
                            mandatory
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: props.questionnaireKey,
                                sectionId
                            }}
                        />
                    </>}
                </>}
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.UrinalysisProtein)}
                    mandatory
                    values={[
                        "Positive",
                        "Negative"
                    ]}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.UrinalysisGlucose)}
                    mandatory
                    values={[
                        "Positive",
                        "Negative"
                    ]}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.UrinalysisBlood)}
                    mandatory
                    values={[
                        "Positive",
                        "Negative"
                    ]}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.UrinalysisSelfReferralLetterIssued)}
                    mandatory
                />
                <p>Locomotor System - Can patient demonstrate full movement of the following?</p>
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfNeck)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfBack)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfLeftShoulder)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfLeftElbow)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfRightShoulder)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfRightElbow)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfLeftWrist)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfLeftLeg)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfRightWrist)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DemonstratedFullMovementOfRightLeg)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoRestrictionsAffectPerformance)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoRestrictionsAffectPerformanceNotes)}
                    mandatory
                    multiline
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionTestType)}
                    options={[
                        { text: "Keystone", value: "Keystone" },
                        { text: "Titmus", value: "Titmus" },
                        { text: "Snellen Chart", value: "Snellen Chart" },
                        { text: otherTestTypeAnswer, value: otherTestTypeAnswer }
                    ]}
                />
                <OldDateQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionServiceDate)}
                />
                <MultiCheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRoleType)}
                >
                    <MultiCheckbox label="Office based - VDU" value="Office based - VDU" />
                    <MultiCheckbox label="Office based + driving on company business" value="Office based + driving on company business" />
                    <MultiCheckbox label="Lift truck driver" value="Lift truck driver" />
                    <MultiCheckbox label="Crate operator" value="Crate operator" />
                    <MultiCheckbox label="DVLA Group 1 - cars" value="DVLA Group 1 - cars" />
                    <MultiCheckbox label="Group 2 LGV (HGV)" value="Group 2 LGV (HGV)" />
                    <MultiCheckbox label="PCV" value="PCV" />
                    <MultiCheckbox label="Other" value={otherRoleTypeAnswer} />
                </MultiCheckboxQuestion>
                {getAnswerToQuestionWithId(questions, questionIds.VisionRoleType) === otherRoleTypeAnswer &&
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionOtherRoleType)}
                    />
                }
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLastOpticianTest)}
                    options={[
                        { text: "Within past two years (in date)", value: "Within past two years (in date)" },
                        {
                            text: "> 2 years; advise attendance at opticians for eye health",
                            value: "> 2 years; advise attendance at opticians for eye health"
                        },
                        {
                            text: "Never had opticians test; advise attendance at opticians for eye health",
                            value: "Never had opticians test; advise attendance at opticians for eye health"
                        },
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionCurrentCorrectionRequired)}
                    options={[
                        { text: noVisualAidAnswer, value: noVisualAidAnswer },
                        { text: "Glasses", value: "Glasses" },
                        { text: "Contacts", value: "Contacts" },
                        { text: "Has had laser correction", value: "Has had laser correction" },
                        { text: "Cataract surgery", value: "Cataract surgery" },
                        { text: otherVisualAidAnswer, value: otherVisualAidAnswer }
                    ]}
                />
                {getAnswerToQuestionWithId(questions, questionIds.VisionCurrentCorrectionRequired) === otherVisualAidAnswer &&
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionCurrentVisualAidDescription)}
                    />
                }
                {!!getAnswerToQuestionWithId(questions, questionIds.VisionCurrentCorrectionRequired) &&
                    getAnswerToQuestionWithId(questions, questionIds.VisionCurrentCorrectionRequired) !== noVisualAidAnswer &&
                    <DropdownQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionCorrectivePrescriptionUsed)}
                        options={[
                            { text: "Distance", value: "Distance" },
                            { text: "Intermediate - VDU distance", value: "Intermediate - VDU distance" },
                            { text: "Reading distance", value: "Reading distance" },
                            { text: "Occupational lenses (multifocals)", value: "Occupational lenses" }
                        ]}
                    />
                }
                <MultiCheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionFamilyHistory)}
                >
                    <MultiCheckbox label="Glaucoma advice regular opticians test" value="Glaucoma advice regular opticians test" />
                    <MultiCheckbox label="Macular degeneration" value="Macular degeneration" />
                    <MultiCheckbox label="Neither of the above" value="Neither of the above" />
                </MultiCheckboxQuestion>
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHistoricalCurrentIssues)}
                    outdated={{
                        isOutdated: true,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHistoricalCurrentIssuesYesOrNo)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                {getAnswerToQuestionWithId(questions, questionIds.VisionHistoricalCurrentIssuesYesOrNo) === MultiButtonQuestion.YesOption && <>
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHistoricalCurrentIssuesDetails)}
                        mandatory
                        multiline
                        outdated={{
                            isOutdated: false,
                            questionnaireKey: props.questionnaireKey,
                            sectionId
                        }}
                    />
                </>}
                <p>Distance Vision Result</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDistanceCorrectionDistance)}
                    options={[{ text: "Corrected", value: "Corrected" }, { text: "Uncorrected", value: "Uncorrected" }]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRightEyeDistance)}
                    options={distanceTestResultOptions}
                    filterOldOptions
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLeftEyeDistance)}
                    options={distanceTestResultOptions}
                    filterOldOptions
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesDistance)}
                    options={distanceTestResultOptions}
                    filterOldOptions
                />
                <p>Intermediate Result</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionIntermediateCorrection)}
                    options={[{ text: "Corrected", value: "Corrected" }, { text: "Uncorrected", value: "Uncorrected" }]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesIntermediate100Cm)}
                    options={intermediateNearTestResultOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesIntermediate60Cm)}
                    options={intermediateNearTestResultOptions}
                />
                <p>Near Vision Result</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDistanceCorrectionNear)}
                    options={[{ text: "Corrected", value: "Corrected" }, { text: "Uncorrected", value: "Uncorrected" }]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRightEyeNear)}
                    options={intermediateNearTestResultOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLeftEyeNear)}
                    options={intermediateNearTestResultOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesNear)}
                    options={intermediateNearTestResultOptions}
                />
                <p>Night Vision</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionNightVision)}
                    options={[
                        { text: "Acceptable", value: "Acceptable" },
                        {
                            text: "Marginal or unacceptable result; advise employee of impact on driving at night and refer to optician",
                            value: "Marginal or unacceptable result; advise employee of impact on driving at night and refer to optician"
                        }
                    ]}
                />
                <p>Colour Vision</p>
                <MultiCheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionColourTestType)}
                >
                    <MultiCheckbox label="Keystone" value="Keystone" />
                    <MultiCheckbox label="Ishihara" value="Ishihara" />
                    <MultiCheckbox label="City University" value="City University" />
                    <MultiCheckbox label="Trade Test" value="Trade Test" />
                </MultiCheckboxQuestion>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionColourResult)}
                    options={[
                        { text: "Three correct: Acceptable", value: "Three correct: Acceptable" },
                        {
                            text: "Two correct: Marginal, may need trade test if in safety critical role e.g. electrician, or trackside",
                            value: "Two correct: Marginal, may need trade test if in safety critical role e.g. electrician, or trackside"
                        },
                        {
                            text: "None or one correct: Unacceptable, refer if safety critical",
                            value: "None or one correct: Unacceptable, refer if safety critical"
                        }
                    ]}
                />
                <p>Peripheral Field of Vision</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLeftEyePeripheralField)}
                    options={eyeVisionPeripheralFieldOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRightEyePeripheralField)}
                    options={eyeVisionPeripheralFieldOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHorizontalField)}
                    options={visionHorizontalFieldOptions}
                />
                <p>Phoria (Eye co-ordination)</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionPhoriaDistance)}
                    options={[
                        { text: "Satisfactory", value: "Satisfactory" },
                        {
                            text: "If outwith parameters and symptoms of headache etc. present, advise optician review",
                            value: "If outwith parameters and symptoms of headache etc. present, advise optician review"
                        }
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionPhoriaNear)}
                    options={[
                        { text: "Satisfactory", value: "Satisfactory" },
                        {
                            text: "If outwith parameters and symptoms of headache etc. present, advise optician review",
                            value: "If outwith parameters and symptoms of headache etc. present, advise optician review"
                        }
                    ]}
                />
                <p>Depth Perception (Crane Ops/LiftTrucks/NDT etc.)</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDepthPerceptionDistance)}
                    options={[
                        { text: "All correct: Acceptable", value: "All correct: Acceptable" },
                        {
                            // eslint-disable-next-line max-len
                            text: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing",
                            // eslint-disable-next-line max-len
                            value: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing"
                        }
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDepthPerceptionNear)}
                    options={[
                        { text: "All correct: Acceptable", value: "All correct: Acceptable" },
                        {
                            // eslint-disable-next-line max-len
                            text: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing",
                            // eslint-disable-next-line max-len
                            value: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing"
                        }
                    ]}
                />
                <p>Client Guidance</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionSafetyGlassesAdequateAndInGoodCondition)}
                    options={[
                        { text: "Yes", value: "Yes" },
                        { text: "No - refer to line manager", value: "No - refer to line manager" },
                        { text: "Not available for inspection", value: "Not available for inspection" }
                    ]}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRemindedToAttendOpticiansRoutinely)}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OpticiansSelfReferralLetterIssued)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EmployeeInformedGlassesAreWornToMeetMinimumStandards)}
                    value="Yes"
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EmployeeInformedGlassesAreWornToMeetMinimumStandardsAdditionalNotes)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                    multiline
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdvisedEmployeeTemporarilyUnfitToCarryOutDuties)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                {getAnswerToQuestionWithId(questions, questionIds.AdvisedEmployeeTemporarilyUnfitToCarryOutDuties) === MultiButtonQuestion.YesOption && <>
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PleaseDocumentTheReasonsForTheAboveDecision)}
                        mandatory
                        multiline
                        outdated={{
                            isOutdated: false,
                            questionnaireKey: props.questionnaireKey,
                            sectionId
                        }}
                    />
                </>}
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdvisedToDiscussTemporarilyUnfitStatusWithLineManager)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.InformedCaseWillBeAssessedByOha)}
                    value="Yes"
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ConfirmIfEmployeeHasProvidedConsentToProvideInformationToEmployer)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId
                    }}
                />
                {/*  eslint-disable-next-line max-len */}
                {getAnswerToQuestionWithId(questions, questionIds.ConfirmIfEmployeeHasProvidedConsentToProvideInformationToEmployer) === MultiButtonQuestion.YesOption && <>
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ProvideDetailsOfTheConsent)}
                        mandatory
                        multiline
                        outdated={{
                            isOutdated: false,
                            questionnaireKey: props.questionnaireKey,
                            sectionId
                        }}
                    />
                </>}
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.GeneralNotes)}
                    multiline
                />
                {props.fileUploadApi &&
                    <FilesQuestion
                        api={props.fileUploadApi}
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MedicalDocuments)}
                        acceptExtensions={acceptableExtensions}
                    />
                }
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AnyDocumentsSentViaPost)}
                    value="Yes"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ConsentGiven)}
                    mandatory
                    value="Yes"
                    hideDivider
                />
            </>}
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

// eslint-disable-next-line max-len
export const TechnicianNotesSection = connect(makeMapStateToQuestionnaireSectionProps(sectionId), mapDispatchToQuestionnaireSectionProps)(TechnicianNotesSectionUnconnected);
